import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { PlusCircle, Eye, TrendingUp } from "react-feather";
import MasterDetailListShell from "../../../../components/layout/MasterDetailListShell";
import MasterDetailRow from "../../../../components/layout/MasterDetailRow";
import { useGetEmployeesQuery } from "../employeesApi";
import { useGetDepartmentsQuery } from "../../departments/departmentsApi";
import { useGetHrDesignationsQuery } from "../../designations/hrDesignationsApi";
import { useCanCreateEmployees, useCanPromoteEmployees } from "../useEmployeePermissions";
import { useBranchFilter } from "../../../branchFilter/useBranchFilter";
import { paths } from "../../../../routes/paths";

const STATUS_OPTIONS = [
  { value: "", label: "All Statuses" },
  { value: "active", label: "Active" },
  { value: "inactive", label: "Inactive" },
];

// "Basic View" pane of the master-detail split (design concept approved
// 2026-08-28, permanent as of 2026-08-29) - built on the reusable
// MasterDetailListShell/MasterDetailRow (extracted 2026-08-29 once Customers
// needed the same pattern), so this file only supplies Employee-specific
// data fetching, filters, and row content.
const EmployeeCompactList = () => {
  const canCreate = useCanCreateEmployees();
  const canPromote = useCanPromoteEmployees();
  const navigate = useNavigate();
  const { recordGuid: selectedRecordGuid } = useParams();
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [departmentId, setDepartmentId] = useState("");
  const [designationId, setDesignationId] = useState("");
  const { selectedBranchId } = useBranchFilter();

  useEffect(() => {
    const handle = setTimeout(() => setSearch(searchInput.trim()), 400);
    return () => clearTimeout(handle);
  }, [searchInput]);

  const { data: employees = [], isLoading } = useGetEmployeesQuery({
    search: search || undefined,
    isActivate: status === "" ? undefined : status === "active",
    branchId: selectedBranchId ?? undefined,
  });
  const { data: departments = [] } = useGetDepartmentsQuery();
  const { data: designations = [] } = useGetHrDesignationsQuery();

  const visibleDesignations = departmentId
    ? designations.filter((d) => String(d.departmentId) === departmentId)
    : designations;

  const filtered = useMemo(
    () =>
      employees
        .filter((e) => !departmentId || String(e.departmentId) === departmentId)
        .filter((e) => !designationId || String(e.designationId) === designationId),
    [employees, departmentId, designationId]
  );

  const initials = (emp) =>
    `${emp.empName?.[0] || ""}${emp.empLastName?.[0] || ""}`.toUpperCase() || "?";

  return (
    <MasterDetailListShell
      title="Employees"
      addButton={
        canCreate && (
          <button type="button" className="btn btn-added" onClick={() => navigate(paths.employeeNew)}>
            <PlusCircle className="me-2" size={16} />
            Add New Employee
          </button>
        )
      }
      filters={
        <>
          <input
            type="text"
            placeholder="Search name or code"
            className="form-control form-control-sm"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
          <div className="d-flex gap-2 mt-2">
            <select
              className="form-control form-control-sm"
              value={departmentId}
              onChange={(e) => {
                setDepartmentId(e.target.value);
                setDesignationId("");
              }}
            >
              <option value="">All Departments</option>
              {departments.map((d) => (
                <option key={d.departmentCode} value={d.departmentCode}>{d.departmentName}</option>
              ))}
            </select>
            <select
              className="form-control form-control-sm"
              value={designationId}
              onChange={(e) => setDesignationId(e.target.value)}
            >
              <option value="">All Designations</option>
              {visibleDesignations.map((d) => (
                <option key={d.designationCode} value={d.designationCode}>{d.designationName}</option>
              ))}
            </select>
            <select
              className="form-control form-control-sm"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
            >
              {STATUS_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
        </>
      }
      isLoading={isLoading}
      emptyLabel="No employees match these filters."
      items={filtered}
      getKey={(emp) => emp.recordGuid}
      resetKey={`${search}|${status}|${departmentId}|${designationId}|${selectedBranchId}`}
      renderItem={(emp) => {
        const isSelected = String(emp.recordGuid) === String(selectedRecordGuid);
        const designation = designations.find(
          (d) => String(d.designationCode) === String(emp.designationId)
        );
        return (
          <MasterDetailRow
            isSelected={isSelected}
            onClick={() => navigate(paths.employeeEdit(emp.recordGuid))}
            avatarLabel={initials(emp)}
            title={
              <>
                {emp.empName} {emp.empLastName}
                <span className="text-muted ms-1">#{emp.empCode}</span>
              </>
            }
            subtitle={
              <>{emp.designationName || designation?.designationName || "-"} &middot; {emp.branchCode}</>
            }
            badge={
              <span className={`badge ${Number(emp.isActivate) ? "badge-linesuccess" : "badge-linedanger"} me-2`}>
                {Number(emp.isActivate) ? "Active" : "Inactive"}
              </span>
            }
            actions={
              <>
                <button
                  type="button"
                  className="btn btn-link p-1"
                  title="View / Edit"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(paths.employeeEdit(emp.recordGuid));
                  }}
                >
                  <Eye size={15} />
                </button>
                {canPromote && (
                  <button
                    type="button"
                    className="btn btn-link p-1"
                    title="Promote"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(paths.employeePromote(emp.recordGuid));
                    }}
                  >
                    <TrendingUp size={15} />
                  </button>
                )}
              </>
            }
          />
        );
      }}
    />
  );
};

export default EmployeeCompactList;
